import React from "react";
// Bill Generation System

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomDataTable from "../table/CustomTable";
import CustomChip from "../chip/Chip";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Svg
import Calendar2 from "../../assets/svg/calendar2";
import Download from "../../assets/svg/download";

// React icons
import { FiFileText } from "react-icons/fi";
import { FaRegClock } from "react-icons/fa";
import { LuTriangle } from "react-icons/lu";
import { FaRegCheckCircle } from "react-icons/fa";
import { FaFilter } from "react-icons/fa";
import { LuRefreshCw } from "react-icons/lu";
import { IoPauseCircleOutline } from "react-icons/io5";
import { FaPlay } from "react-icons/fa";
import { IconType } from "react-icons";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface StatsData {
  title: string;
  amount: string;
  subTitle: string;
  icon: IconType;
}

interface ExceptionSummary {
  title: string;
  amount: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface Batch {
  id: string;
  meters: number;
  route: string;
  progress: number;
  exceptions: number;
  startTime: string;
  status: string;
}

const BillGenerationSystem: React.FC = () => {
  const COLUMNS = [
    "Batch Details",
    "Route",
    "Progress",
    "Exceptions",
    "Start Time",
    "Status",
    "Actions",
  ];

  const cycleData = {
    totalMeters: 12458,
    processed: 11985,
    exceptions: 473,
    estimatedTime: "1.5 hrs",
    currentCycle: "January 2024",
  };

  const statsData: StatsData[] = [
    {
      title: "Total Meters",
      amount: "12458",
      subTitle: "in current cycle",
      icon: FiFileText,
    },
    {
      title: "Processed",
      amount: "11985",
      subTitle: "complete",
      icon: FaRegCheckCircle,
    },
    {
      title: "Exceptions",
      amount: "473",
      subTitle: "Require review",
      icon: LuTriangle,
    },
    {
      title: "Estimated Time",
      amount: "1.5 hrs",
      subTitle: "Remaining",
      icon: FaRegClock,
    },
  ];

  const exceptionSummary: ExceptionSummary[] = [
    {
      title: "Missing Reads",
      amount: "245",
    },
    {
      title: "Rate Errors",
      amount: "128",
    },
    {
      title: "Validation Failures",
      amount: "85",
    },
    {
      title: "Other Issues",
      amount: "15",
    },
  ];

  const statsCard = ({
    title,
    amount,
    subTitle,
    icon,
  }: {
    title: string;
    amount: string;
    subTitle: string;
    icon: IconType;
  }) => {
    return (
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack>
              <Typography variant="h5">{title}</Typography>
              <Typography variant="body1" mt={"10px"} fontWeight={700}>
                {amount}
              </Typography>
              {title === "Processed" ? (
                <Typography variant="caption" mt={"10px"}>
                  {(
                    (cycleData.processed / cycleData.totalMeters) *
                    100
                  ).toFixed(1)}
                  % {subTitle}
                </Typography>
              ) : (
                <Typography variant="caption" mt={"10px"}>
                  {subTitle}
                </Typography>
              )}
            </Stack>
            <UnifiedIcon icon={icon} color="#999B9D" fontSize="30px" />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const statusOptions: SelectOption[] = [
    { value: "all", label: "All Status" },
    { value: "completed", label: "Completed" },
    { value: "in-progress", label: "In Progress" },
    { value: "pending", label: "Pending" },
  ];

  const batchesOptions: SelectOption[] = [
    {
      value: "BATCH-001",
      label: "Route 1",
    },
    {
      value: "BATCH-002",
      label: "Route 2",
    },
    {
      value: "BATCH-003",
      label: "Route 3",
    },
  ];

  const batches: Batch[] = [
    {
      id: "BATCH-001",
      meters: 2500,
      route: "Route 1",
      progress: 99.2,
      exceptions: 20,
      startTime: "2024-02-01 08:00 AM",
      status: "Completed",
    },
    {
      id: "BATCH-002",
      meters: 3000,
      route: "Route 2",
      progress: 95.0,
      exceptions: 150,
      startTime: "2024-02-01 09:31 AM",
      status: "In Progress",
    },
    {
      id: "BATCH-003",
      meters: 2800,
      route: "Route 3",
      progress: 0.0,
      exceptions: 0,
      startTime: "-",
      status: "Pending",
    },
  ];

  const chipColor = (status: string): string => {
    if (status === "Completed") return "#9CE976";
    if (status === "In Progress") return "#edb64a";
    if (status === "Pending") return "#0396FF";
    return "#999B9D";
  };

  const rowsContent = batches?.map((item: Batch, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.id} {item.meters}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.route}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.progress}%
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.exceptions}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          {item.startTime}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: 0 }}>
        <Typography className="custom_table_tbody_cell">
          <CustomChip
            label={item.status}
            backgroundColor={chipColor(item.status)}
          />
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: 0 }}>
        <Stack direction={"row"} alignContent={"center"} gap={"15px"}>
          <UnifiedIcon icon={LuRefreshCw} color="#999B9D" fontSize="16px" />
          <UnifiedIcon
            icon={IoPauseCircleOutline}
            color="#999B9D"
            fontSize="18px"
          />
          <UnifiedIcon icon={FaPlay} color="#999B9D" fontSize="16px" />
        </Stack>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack>
            <Stack>
              <Typography variant="h5">Bill Generation</Typography>
              <Typography variant="caption" color="#7D808F" mt={"2px"}>
                Current Billing Cycle: {cycleData.currentCycle}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
            <Calendar2 />
            <ContainedButton
              label={"Start Generation"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2.25} mt={"20px"}>
        {statsData.map((item: StatsData, index: number) => {
          return (
            <Grid key={index} size={{ xs: 12, md: 3, lg: 3 }}>
              {statsCard(item)}
            </Grid>
          );
        })}
      </Grid>

      <Card sx={{ mt: "20px", maxHeight: "650px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Stack
                direction={"row"}
                gap={"10px"}
                alignItems={"center"}
                width={"100%"}
              >
                <Box sx={{ width: "25%" }}>
                  <CustomOutlinedSelect
                    placeholder={"Select"}
                    options={statusOptions}
                  />
                </Box>
                <Box sx={{ width: "25%" }}>
                  <CustomOutlinedSelect
                    placeholder={"Select"}
                    options={batchesOptions}
                  />
                </Box>
              </Stack>
              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <Download />
                <UnifiedIcon icon={FaFilter} color="#4C5269" fontSize="16px" />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <CustomDataTable
            sx={{ maxHeight: "600px" }}
            columns={COLUMNS}
            pagination={false}
            rowsContent={rowsContent}
          />
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Exception Summary</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {exceptionSummary.map((item: ExceptionSummary, index: number) => (
              <Grid key={index} size={{ xs: 12, md: 4, lg: 4 }}>
                <Card sx={{ border: "1px solid #D7DAE7", padding: "16px" }}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      variant="body1"
                      fontSize={"16px"}
                      fontWeight={600}
                    >
                      {item?.title}
                    </Typography>
                    <Typography variant="caption" fontSize={"14px"}>
                      {item?.amount}
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default BillGenerationSystem;
